import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { FaEye, FaEyeSlash } from 'react-icons/fa6';
import { forgotPassword, resetPassword, verifyForgotOtp } from '../../store/auth/authActions';
import { errorToast, isValidEmail } from '../../utils';

const ForgotPassword = () => {
	const { loading } = useSelector((state) => state.auth);
	const [step, setStep] = useState(1);
	const [emailValue, setEmailValue] = useState('');
	const [otpValue, setOtpValue] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [passwordError, setPasswordError] = useState('');
	const [otpError, setOtpError] = useState('');
	const [emailError, setEmailError] = useState('');
	const [stepLoading, setStepLoading] = useState(false);
	const [resetSuccess, setResetSuccess] = useState(false);
	const dispatch = useDispatch();
	let email = useRef('');
	let code = useRef('');
	let password = useRef('');
	let confirmPassword = useRef('');


	const requestCode = async (e) => {
		e.preventDefault();
		const submittedEmail = email?.value?.trim() || '';

		if (!isValidEmail(submittedEmail)) {
			setEmailError('Please enter a valid email address');
			return;
		}
		setEmailError('');

		dispatch(forgotPassword({
			email: submittedEmail,
		}))
			.unwrap()
			.then(() => {
				setEmailValue(submittedEmail);
				setStep(2)
			})
			.catch((errorData) => {
				errorToast(errorData.error);
			});
	};

	const verifyOtpStep = async (e) => {
		e.preventDefault();
		const submittedCode = code?.value?.trim() || '';
		if (!submittedCode || submittedCode.length !== 6) {
			setOtpError('Please enter a valid 6-digit code');
			return;
		}
		setOtpError('');
		setStepLoading(true);
		dispatch(verifyForgotOtp({ email: emailValue, code: submittedCode }))
			.unwrap()
			.then(() => {
				setOtpValue(submittedCode);
				setStep(3);
			})
			.catch((errorData) => {
				setOtpError(errorData.error || 'Invalid or expired code');
			})
			.finally(() => setStepLoading(false));
	};

	const submitReset = async (e) => {
		e.preventDefault();
		if (!otpValue || otpValue.length !== 6) {
			setStep(2);
			setOtpError('Please enter a valid 6-digit code');
			return;
		}
		if (!password.value || password.value.trim().length < 8) {
			setPasswordError('Password must be at least 8 characters');
			return;
		}
		if (password.value !== confirmPassword.value) {
			setPasswordError('Passwords do not match');
			return;
		}
		setPasswordError('');

		setStepLoading(true);
		dispatch(resetPassword({
			email: emailValue,
			code: otpValue,
			password: password.value,
		}))
			.unwrap()
			.then(() => {
				setResetSuccess(true);
			})
			.catch((errorData) => {
				errorToast(errorData.error);
			})
			.finally(() => setStepLoading(false));
	};

	const onSubmitForm = step === 1 ? requestCode : step === 2 ? verifyOtpStep : submitReset;

	if (resetSuccess) {
		return (
			<div className="space-y-5 text-center">
				<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
					<svg className="h-8 w-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
						<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
					</svg>
				</div>
				<div>
					<h1 className="mb-1 text-nt-blue">Password reset!</h1>
					<p className="text-sm text-nt-grey">Your password has been changed successfully. You can now log in with your new password.</p>
				</div>
				<Link
					to="/login"
					className="block w-full rounded-xl bg-nt-blue px-4 py-2.5 text-center text-white transition hover:opacity-95"
				>
					Go to Login
				</Link>
			</div>
		);
	}

	return (
		<>
			<Toaster />
			<form onSubmit={onSubmitForm} className="space-y-4">
			<h1 className='text-nt-blue text-center'>Forgot password</h1>
			<p className='text-center text-sm text-nt-grey'>
				{step === 1 ? 'Enter your email to receive OTP.' : step === 2 ? 'Enter the OTP sent to your email.' : 'Set your new password.'}
			</p>
			<div className='flex items-center justify-center gap-2 text-xs'>
				<span className={`${step >= 1 ? 'bg-nt-blue text-white' : 'bg-nt-grey-light text-nt-grey'} rounded-full px-2.5 py-1`}>Email</span>
				<span className={`${step >= 2 ? 'bg-nt-blue text-white' : 'bg-nt-grey-light text-nt-grey'} rounded-full px-2.5 py-1`}>OTP</span>
				<span className={`${step >= 3 ? 'bg-nt-blue text-white' : 'bg-nt-grey-light text-nt-grey'} rounded-full px-2.5 py-1`}>Reset</span>
			</div>
			{step === 1 && (
				<div>
					<label className="block text-primary-grey text-[13px] font-medium pb-1">Email</label>
					<div className="relative">
						<input
							className="inputField w-full"
							name="email"
							placeholder="Enter your email"
							id="email"
							ref={(e) => { email = e; }}
							defaultValue={""}
							type="email"
							onChange={() => setEmailError('')}
							required
						/>
					</div>
					{emailError ? <span className='mt-1 block text-xs text-nt-red'>{emailError}</span> : null}
				</div>
			)}
			{step === 2 && (
				<div>
					<label className="block text-primary-grey text-[13px] font-medium pb-1">OTP Code</label>
					<input
						className="inputField w-full"
						name="otp"
						placeholder="Enter 6-digit code"
						id="otp"
						ref={(e) => { code = e; }}
						type="text"
						maxLength={6}
						required
						onChange={() => setOtpError('')}
					/>
					{otpError ? <span className='mt-1 block text-xs text-nt-red'>{otpError}</span> : null}
				</div>
			)}
			{step === 3 && (
				<div>
					<label className="block text-primary-grey text-[13px] font-medium pb-1">New Password</label>
					<div className="relative">
						<input
							className="inputField w-full pr-10"
							name="password"
							placeholder="Enter your new password"
							id="password"
							ref={(e) => { password = e; }}
							type={showPassword ? 'text' : 'password'}
							onChange={() => setPasswordError('')}
							required
						/>
						<button
							type="button"
							className="absolute right-2 top-1/2 -translate-y-1/2 text-nt-grey hover:text-nt-blue"
							onClick={() => setShowPassword((v) => !v)}
							aria-label={showPassword ? "Hide password" : "Show password"}
							title={showPassword ? "Hide password" : "Show password"}
						>
							{showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
						</button>
					</div>
					<div className="mt-3">
						<label className="block text-primary-grey text-[13px] font-medium pb-1">Confirm Password</label>
						<div className="relative">
							<input
								className="inputField w-full pr-10"
								name="confirmPassword"
								placeholder="Confirm your new password"
								id="confirmPassword"
								ref={(e) => { confirmPassword = e; }}
								type={showPassword ? 'text' : 'password'}
								onChange={() => setPasswordError('')}
								required
							/>
							<button
								type="button"
								className="absolute right-2 top-1/2 -translate-y-1/2 text-nt-grey hover:text-nt-blue"
								onClick={() => setShowPassword((v) => !v)}
								aria-label={showPassword ? "Hide password" : "Show password"}
								title={showPassword ? "Hide password" : "Show password"}
							>
								{showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
							</button>
						</div>
					</div>
					{passwordError ? <span className='mt-1 block text-xs text-nt-red'>{passwordError}</span> : null}
				</div>
			)}
			<button type="submit" className="primaryButton mt-4" disabled={loading || stepLoading}>
				{loading || stepLoading ? 'Loading...' : (step === 1 ? 'Send OTP' : step === 2 ? 'Verify OTP' : 'Reset Password')}
			</button>
			{step >= 2 && <p className="text-center mt-4">
				OTP sent successfully. Check your email and continue the reset flow.
			</p>}
			<p className='text-center mt-5'>Already know your password ? <br /> you can <Link to='/login' className="link">login here.</Link>
			</p>
		</form>
		</>
	);
};

export { ForgotPassword }